import { Divider, Select } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { ExecuteReportValues, AdaptedValues } from "shared/types";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { adaptPackagesData } from "shared/adapters";
import { useGetAllPackagesWithoutParamsQuery } from "shared/api";
import { Colors } from "shared/themes";
import { FormSelect } from "shared/ui";

interface PackagingFilterProps {
  adaptedOrders: AdaptedValues[];
  selectedOrderId: number | null;
  onOrderChange: (value: number) => void;
  onOrderSearch: (value: string) => void;
  isLoadingOrders: boolean;
}

const PackagingFilter: FC<PackagingFilterProps> = ({
  selectedOrderId,
  onOrderChange,
  onOrderSearch,
  adaptedOrders,
  isLoadingOrders,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const { values, setFieldValue, handleBlur } =
    useFormikContext<ExecuteReportValues>();

  const { data: packages } = useGetAllPackagesWithoutParamsQuery(
    { order: selectedOrderId ?? undefined },
    { skip: !selectedOrderId }
  );

  const adaptedPackages = useMemo(() => {
    return adaptPackagesData(packages ?? []);
  }, [packages]);

  return (
    <>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
      <div className="flex gap-x-3">
        <div className="flex flex-col gap-y-0.5">
          <div className="flex gap-x-2">
            <Title level={5} className="whitespace-nowrap !m-0">
              {t("selectOrder")}
            </Title>
            {" - "}
            <Select
              loading={isLoadingOrders}
              value={selectedOrderId}
              placeholder={t("selectOrder")}
              options={adaptedOrders}
              onChange={onOrderChange}
              onSearch={onOrderSearch}
              className="w-full"
              size="small"
              showSearch
              filterOption={false}
            />
          </div>
          <span className="text-[10px] leading-tight text-gray-400">
            * {t("orderSearchHint")}
          </span>
        </div>

        {selectedOrderId && (
          <>
            <Divider
              type="vertical"
              className="h-full"
              style={{ background: Colors.blue.primary }}
            />

            <div className="flex gap-x-2">
              <Title level={5} className="!w-[140px]  !m-0">
                {t("selectPackage")}
              </Title>
              {" - "}
              <FormSelect
                value={values?.packaging_id}
                placeholder={t("selectPackage")}
                fieldName="packaging_id"
                options={adaptedPackages}
                setFieldValue={setFieldValue}
                handleBlur={handleBlur}
                showSearch
                size="small"
                className="!m-0"
              />
            </div>
          </>
        )}
      </div>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
    </>
  );
};

export { PackagingFilter };
